import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Download, Mail, Globe, Trash2, Undo2, Loader2, RefreshCw } from 'lucide-react';
import EmailSendHistory from '../../components/shared/EmailSendHistory';
import { toast } from 'sonner';
import api from '../../services/api';
import ReportEmailDialog from '../../components/reports/ReportEmailDialog';
import DailySaleUpdateDocument from '../../components/reports/DailySaleUpdateDocument';
import DepartureFormDocument from '../../components/reports/DepartureFormDocument';
import { exportToCSV } from '../../utils/exportCSV';

const fmt = (v) => v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v) : '-';

const GREEN_DOOR_LOGO = 'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

const CURRENCY_FIELDS = ['sale_price', 'sale_company_commission', 'commission', 'entry_fee', 'other_fees', 'total_fees', 'net_proceeds', 'owner_net_proceeds', 'amount',
  'all_time_commissions', 'ytd_commissions', 'all_time_revenue', 'ytd_revenue', 'avg_price', 'median_price', 'total_revenue', 'reserve_price'];

const SavedReportDetailPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const qc = useQueryClient();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const { data: report, isLoading: loading } = useQuery({
    queryKey: QK.savedReport(reportId),
    queryFn: () => api.get(`/reports/saved/${reportId}`).then(r => r.data),
    enabled: !!reportId,
    onError: () => toast.error('Failed to load report'),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: QK.savedReport(reportId) });
    // Use prefix key ['saved-reports'] so it invalidates QK.savedReports(tenantId) variants
    qc.invalidateQueries({ queryKey: QK.savedReportsPrefix() });
  };

  const handlePublish = async () => {
    try {
      await api.post(`/reports/saved/${reportId}/publish-to-portal`);
      toast.success('Report published to portal');
      invalidate();
    } catch { toast.error('Failed to publish'); }
  };

  const handleUnpublish = async () => {
    try {
      await api.post(`/reports/saved/${reportId}/unpublish-from-portal`);
      toast.success('Report removed from portal');
      invalidate();
    } catch { toast.error('Failed to unpublish'); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/reports/saved/${reportId}/refresh`);
      toast.success('Report data refreshed with latest viewing times');
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to refresh report data');
    }
    setRefreshing(false);
  };

  const handleDelete = () => {
    setConfirmAction({
      message: 'Delete this saved report?',
      onConfirm: async () => {
        try {
          await api.delete(`/reports/saved/${reportId}`);
          toast.success('Report deleted');
          navigate('/reports');
        } catch { toast.error('Failed to delete'); }
        setConfirmAction(null);
      }
    });
  };

  const getRows = () => {
    const data = report?.report_data;
    if (!data) return [];
    if (report.report_type === 'daily-sale-updates') return data.rows || [];
    if (report.report_type === 'tenant-financials') return data.accounts || [];
    if (report.report_type === 'net-proceeds') return data.entries || [];
    if (report.report_type === 'outstanding-receivables') return data.invoices || [];
    if (report.report_type === 'custom') return data.rows || [];
    return Array.isArray(data) ? data : [];
  };

  const renderCell = (row, col) => {
    const val = row[col.key];
    if (val === null || val === undefined) return '-';
    if (col.key === 'bucket') {
      const colors = { current: 'bg-gray-100 text-gray-700', '30': 'bg-yellow-100 text-yellow-700', '60': 'bg-orange-100 text-orange-700', '90+': 'bg-red-100 text-red-700' };
      return <Badge className={`text-xs pointer-events-none ${colors[val] || ''}`}>{val === 'current' ? 'Current' : `${val} Days`}</Badge>;
    }
    if (col.key === 'sold_rate') return <Badge className={`text-xs pointer-events-none ${val > 70 ? 'bg-green-100 text-green-700' : val > 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{val}%</Badge>;
    if (col.key === 'rating') return <Badge variant="outline" className="text-xs pointer-events-none">{val || '-'}</Badge>;
    // Custom reports use col.type for formatting
    if (col.type === 'currency') return <span className="font-medium">{fmt(val)}</span>;
    if (col.type === 'boolean') return val ? 'Yes' : 'No';
    if (col.type === 'date' && val) {
      try { const d = new Date(val); return isNaN(d) ? String(val) : d.toLocaleDateString(); } catch { return String(val); }
    }
    if (col.type === 'datetime' && val) {
      try { return new Date(val).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return String(val); }
    }
    if (CURRENCY_FIELDS.includes(col.key)) {
      const color = col.key === 'net_proceeds' ? 'text-primary font-bold' : col.key === 'total_fees' ? 'text-red-600' : 'font-medium';
      return <span className={color}>{fmt(val)}</span>;
    }
    if (['due_date', 'invoice_date', 'session_date'].includes(col.key) && val) return new Date(val).toLocaleDateString();
    if (col.key === 'hip_number') return `#${val || 'TBD'}`;
    return String(val);
  };

  if (loading) return <SidebarLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></SidebarLayout>;
  if (!report) return <SidebarLayout><div className="text-center py-20 text-muted-foreground">Report not found</div></SidebarLayout>;

  const isDailySaleUpdate = report.report_type === 'daily-sale-updates';
  const isDepartureForm   = report.report_type === 'departure-form';

  // Departure form: always compute display name from report_data so old/new reports are consistent
  const departureFormName = isDepartureForm ? (() => {
    const rd    = report.report_data || {};
    const horse = rd.horse || {};
    const travel = rd.travel || {};
    const dir  = travel.travel_direction === 'from_farm' ? 'On Farm'
                : travel.travel_direction === 'to_farm'  ? 'Returning'
                : 'Travel';
    const dest  = travel.travel_destination_name || travel.travel_dest_other || '';
    const raw   = travel.start_date || travel.next_due_date || '';
    const rawT  = travel.preferred_time || '';
    let dateLabel = '';
    if (raw) { try { dateLabel = new Date(raw + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }); } catch { dateLabel = raw; } }
    let timeLabel = '';
    if (rawT) { try { const [h,m] = rawT.split(':').map(Number); timeLabel = ` @ ${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; } catch {} }
    const travelPart = `${dir}${dest ? ` -> ${dest}` : ''}${dateLabel ? ` (${dateLabel}${timeLabel})` : ''}`;
    return `Departure Form - ${horse.system_name || horse.name || 'Horse'} - ${travelPart}`;
  })() : null;
  const rows = getRows();
  const columns = report.columns || [];
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <SidebarLayout>
      <div className="space-y-4">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
          </Button>
          <Badge className="bg-green-100 text-green-700">Approved</Badge>
        </div>

        {/* Action Bar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api.get(`/reports/saved/${reportId}/pdf`, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${((departureFormName || report.report_name) || 'Report').replace(/[^a-zA-Z0-9._-]/g, '-')}.pdf`;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    // 60s gives the browser time to start the download before revoking
                    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                  } catch(err) {
                    console.error('PDF download error:', err);
                    toast.error('Failed to generate PDF');
                  }
                }}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                {report.report_type !== 'daily-sale-updates' && report.report_type !== 'departure-form' && (
                  <Button size="sm" variant="outline" onClick={() => {
                    const cols = report.columns || [];
                    exportToCSV(rows, cols.map(c => ({ key: c.key, label: c.label, accessor: r => r[c.key] })), report.report_name || 'report');
                  }}>
                    <Download className="w-4 h-4 mr-2" /> Download CSV
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEmailDialogOpen(true)} className={report.emailed ? 'hidden' : ''}>
                  <Mail className="w-4 h-4 mr-2" /> Email via Green Door
                </Button>
                {!report.published_to_portal && (report.crm_entity_id || report.report_type === 'departure-form') && (
                  <Button size="sm" variant="outline" onClick={handlePublish}>
                    <Globe className="w-4 h-4 mr-2" /> Publish to Portal
                  </Button>
                )}
                <div className="ml-auto">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={handleDelete}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Email history */}
              {report.emailed && (
                <EmailSendHistory
                  sends={report.email_sends && report.email_sends.length > 0
                    ? report.email_sends
                    : [{ sent_at: report.emailed_at, sent_by: report.emailed_by, sent_to: report.emailed_to, eml_file_id: report.emailed_eml_file_id }]}
                  onResend={() => setEmailDialogOpen(true)}
                  emlFilename="report_email.eml"
                />
              )}

              {/* Publish status */}
              {report.published_to_portal && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                    <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-700 font-medium">
                      Published to Portal{report.crm_entity_name ? ` for ${report.crm_entity_name}` : ''}
                      {report.published_to_portal_at ? ` on ${fmtDate(report.published_to_portal_at)}` : ''}
                      {report.published_to_portal_by ? ` by ${report.published_to_portal_by}` : ''}
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600" onClick={handleUnpublish}>
                    <Undo2 className="w-4 h-4 mr-1" /> Undo Publish
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Sale Update — use shared document component */}
        {isDailySaleUpdate ? (
          <DailySaleUpdateDocument data={report.report_data} columns={columns} />
        ) : isDepartureForm ? (
          <DepartureFormDocument data={report.report_data} isApproved={report.report_status !== 'draft'} />
        ) : (
          /* Standard table layout for other report types */
          <Card>
            <CardContent className="p-8 space-y-6">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  {report.tenant_logo_url && <img src={report.tenant_logo_url} alt="Logo" className="h-16 mb-2 object-contain" />}
                  {!report.tenant_logo_url && <p className="font-serif text-2xl font-bold">{report.tenant_name}</p>}
                </div>
                <div className="text-right">
                  <p className="font-serif text-3xl font-bold text-muted-foreground mb-2">REPORT</p>
                  <p className="text-sm font-medium">{report.tenant_name}</p>
                </div>
              </div>

              {/* Report meta */}
              <div className="flex justify-between border-t border-b py-4">
                <div>
                  <p className="text-xs text-muted-foreground font-semibold mb-1">{(departureFormName || report.report_name)?.toUpperCase()}</p>
                  {report.crm_entity_name && <p className="text-sm font-semibold">{report.crm_entity_name}</p>}
                </div>
                <div className="text-right text-sm space-y-1">
                  {report.filters?.start_date && (
                    <div><span className="text-muted-foreground">Period: </span><span className="font-medium">{new Date(report.filters.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — {report.filters.end_date ? new Date(report.filters.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Present'}</span></div>
                  )}
                  <div><span className="text-muted-foreground">Saved: </span><span className="font-medium">{report.saved_at ? new Date(report.saved_at).toLocaleDateString() : '-'}</span></div>
                  <div><span className="text-muted-foreground">By: </span><span className="font-medium">{report.saved_by || '-'}</span></div>
                </div>
              </div>

              {/* Summary Cards */}
              {report.summary_data && (
                <div className="flex gap-4 flex-wrap">
                  {Object.entries(report.summary_data).map(([label, value]) => (
                    <div key={label} className="p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className="text-lg font-bold text-primary">{typeof value === 'number' ? fmt(value) : value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Data Table */}
              {columns.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map(c => <TableHead key={c.key}>{c.label}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow><TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">No data</TableCell></TableRow>
                    ) : rows.map((row, i) => (
                      <TableRow key={row.id || i}>
                        {columns.map(c => (
                          <TableCell key={c.key} className="text-sm">{renderCell(row, c)}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <div className="pt-8 flex justify-center">
                <img src={GREEN_DOOR_LOGO} alt="Powered by Green Door" className="w-1/4 mx-auto opacity-60" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Email Dialog */}
      <ReportEmailDialog
        open={emailDialogOpen}
        onClose={(sent) => { setEmailDialogOpen(false); if (sent) invalidate(); }}
        reportId={reportId}
        reportName={report?.report_name}
        reportType={report?.report_type}
      />

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Confirm</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{confirmAction?.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmAction?.onConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

export default SavedReportDetailPage;
